import { schEmptyString, schUSPhoneNumber, schZipCode } from '@aloft/toolkit-shared';
import { Button, Flex, Input, SimpleInput } from '@aloft/toolkit-sui';

import { Controller, FormProvider, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { useZodForm } from '../../hooks/useZodForm.ts';
import { AccountModuleDeps } from './types.ts';

const schContactForm = z.object({
  phone: schUSPhoneNumber.or(schEmptyString),
  zip: schZipCode.or(schEmptyString),
});
type ContactForm = z.infer<typeof schContactForm>;

type Props = {
  account: AccountModuleDeps['account'];
  updateAccount: AccountModuleDeps['updateAccount'];
  isUpdatingAccount: AccountModuleDeps['isUpdatingAccount'];
};

export const ContactInformationContent = ({ account, updateAccount, isUpdatingAccount }: Props) => {
  const makeToast = useToaster();

  const form = useZodForm({
    defaultValues: {
      zip: z.string().parse(account.address.PostalCode ?? ''),
      phone: z.string().parse(account.phone ?? ''),
    },
    schema: schContactForm,
  });

  const onSubmit: SubmitHandler<ContactForm> = async (data) => {
    await updateAccount({
      ...account,
      phone: data.phone || undefined,
      address: {
        ...account.address,
        PostalCode: data.zip || undefined,
      },
    });
    makeToast({
      id: 'update-contact-success',
      title: 'Contact information saved',
      type: 'success',
    });
    form.reset(data);
  };

  const {
    formState: { errors, isDirty },
  } = form;

  return (
    <FormProvider {...form}>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <SimpleInput
          label="Email address"
          type="text"
          placeholder={account.email}
          disabled
          value={account.email}
        />
        <Controller
          name="phone"
          control={form.control}
          render={({ field }) => (
            <Input
              {...field}
              type="phone"
              label="Phone number"
              isInvalid={!!errors.phone}
              onClear={() => field.onChange('')}
            />
          )}
        />
        <Controller
          name="zip"
          control={form.control}
          render={({ field }) => <SimpleInput {...field} label="Zip code" invalid={!!errors.zip} />}
        />
        <Flex alignSelf="flex-end" gap="6px">
          <Button
            text="Cancel"
            onClick={() => form.reset()}
            disabled={!isDirty}
            variant="secondary"
          />
          <Button
            text="Save"
            onClick={form.handleSubmit(onSubmit)}
            disabled={!isDirty || isUpdatingAccount}
            isLoading={isUpdatingAccount}
          />
        </Flex>
      </form>
    </FormProvider>
  );
};
